import React, { FunctionComponent, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import { usePost } from "../../../../api-client/usePost";
import { useLanguage } from "../../../../i18n/useLanguage";
import { useGetVehicleInfo } from "../../../../queries/useGetVehicleInfo";
import { useDealerAccountInfo } from "../../../../stores/hooks/useDealerAccountInfo";
import { AnyPartItem } from "../../../../stores/slices/cartSlice";
import { Button, PrimaryButton } from "../../../../ui/Button/Button";
import { Input } from "../../../../ui/Input/Input";
import { useTabPanels } from "../../../../ui/TabPanels/TabPanels";
import { TabButton } from "../../../../ui/Tabs/TabButton";
import { TabsGroup } from "../../../../ui/Tabs/TabsGroup";
import { Textarea } from "../../../../ui/Textarea/Textarea";
import { useForm } from "../../../../util/useForm";
import { SectionHeading } from "../SectionHeading";

type DealerFormProps = {
  allowPricing: boolean;
  part: AnyPartItem | null;
};

const labelClass = "text-xs font-thin text-ink-2";
const fieldsetClass = "flex flex-col gap-2";

const schema = z
  .object({
    customerContactMethodPreference: z.enum(["0", "1"]),
    customerEmail: z.union([
      z.literal(""),
      z.string().email({ message: "form.error.email" }),
    ]),
    customerFirstName: z.string().nonempty({ message: "form.error.name" }),
    customerMessage: z.string(),
    customerPhone: z.string().optional(),
  })
  .refine(
    (data) =>
      data.customerContactMethodPreference === "0"
        ? !!data.customerEmail
        : true,
    {
      message: "form.error.email",
      path: ["customerEmail"],
    }
  )
  .refine(
    (data) =>
      data.customerContactMethodPreference === "1"
        ? !!data.customerPhone
        : true,
    {
      message: "form.error.tel",
      path: ["customerPhone"],
    }
  );

export const ContactLeadForm: FunctionComponent<DealerFormProps> = ({
  allowPricing,
  part,
}) => {
  const { t } = useTranslation();
  const [customerLanguage] = useLanguage();

  const [state, setState] = useState<"0" | "1">("0");
  const isEmail = state === "0";
  const { data: vehicleInfo } = useGetVehicleInfo();
  const { dealerId } = useDealerAccountInfo();
  const { loading, send } = usePost(
    "/retailSiteWebService/shopping/lead/createItemContactLead.php"
  );

  const { next, prev } = useTabPanels();
  const ref = useRef<HTMLFormElement>(null);

  const [handleForm] = useForm({
    onSubmit({
      customerContactMethodPreference,
      customerEmail,
      customerFirstName,
      customerMessage,
      customerPhone,
    }) {
      send({
        customerContactMethodPreference,
        customerEmail: customerEmail || "",
        customerFirstName,
        customerLanguage,
        customerLastName: "",
        customerMessage,
        customerPhone: customerPhone || "",
        partId: part?.partDetails.primaryPart.itemId || "null",
        partType: part?.partDetails.primaryPart.itemType || "null",
        targetDealerId: dealerId,
        vehicleId: vehicleInfo?.vehicleId || "null",
      }).then(() => {
        ref.current?.reset();
        next();
      });
    },
    schema,
  });

  return (
    <>
      <SectionHeading translation="searchResults.requestMoreInformation" />
      <form className="grid gap-6" noValidate onSubmit={handleForm} ref={ref}>
        <p className={labelClass}>{t("searchResults.dealerFormDescription")}</p>
        {/* name */}
        <fieldset className={fieldsetClass}>
          <label className={labelClass} htmlFor="dealer-form-first-name">
            {t("form.label.name")}
          </label>
          <Input
            aria-label="yourFirstName"
            autoComplete="full-name"
            id="dealer-form-first-name"
            name="customerFirstName"
            placeholder={t("form.placeholder.name")}
          />
        </fieldset>
        {/* contact switch */}
        <fieldset className={fieldsetClass}>
          <label className={labelClass} htmlFor="dealer-form-contact-method">
            {t("searchResults.contactMethod")}
          </label>
          <TabsGroup className="grid grid-cols-2" id="contact-method-tabs">
            <TabButton
              aria-label="showEmailField"
              id={isEmail ? "dealer-form-contact-method" : undefined}
              onClick={(): void => setState("0")}
              selected={isEmail}
              type="button"
            >
              {t("searchResults.email")}
            </TabButton>
            <TabButton
              aria-label="showTelField"
              id={!isEmail ? "dealer-form-contact-method" : undefined}
              onClick={(): void => setState("1")}
              selected={!isEmail}
              type="button"
            >
              {t("searchResults.tel")}
            </TabButton>
          </TabsGroup>
          <input
            name="customerContactMethodPreference"
            type="hidden"
            value={state}
          />
        </fieldset>
        {/* email/tel */}
        {isEmail ? (
          <fieldset className={fieldsetClass}>
            <label className={labelClass} htmlFor="dealer-form-email">
              {t("form.label.email")}
            </label>
            <Input
              aria-label="yourEmail"
              autoCapitalize="off"
              autoComplete="email"
              id="dealer-form-email"
              name="customerEmail"
              placeholder={t("form.placeholder.email")}
              type="email"
            />
          </fieldset>
        ) : (
          <fieldset className={fieldsetClass}>
            <label className={labelClass} htmlFor="dealer-form-tel">
              {t("form.label.tel")}
            </label>
            <Input
              aria-label="yourPhone"
              autoCapitalize="off"
              autoComplete="tel-national"
              id="dealer-form-tel"
              name="customerPhone"
              placeholder={t("form.placeholder.tel")}
              type="tel"
            />
          </fieldset>
        )}
        <fieldset className={fieldsetClass}>
          <label className={labelClass} htmlFor="dealer-form-message">
            {t("form.label.optionalMessage")}
          </label>
          <Textarea
            aria-label="yourNotes"
            autoComplete="off"
            className="box-content"
            id="dealer-form-message"
            name="customerMessage"
            placeholder={t("form.placeholder.optionalMessage")}
          />
        </fieldset>
        <div className="grid gap-2 mt-8">
          <PrimaryButton
            aria-label="sendRequest"
            busy={loading}
            className="h-10 md:h-12"
            type="submit"
          >
            {t("searchResults.sendRequest")}
          </PrimaryButton>
          {allowPricing && (
            <Button
              aria-label="backToQuote"
              className="bg-input border border-edge"
              onClick={prev}
              type="button"
            >
              {t("searchResults.backToQuote")}
            </Button>
          )}
        </div>
      </form>
    </>
  );
};
